import React, { useEffect, useState } from 'react';
import { Container, Row, Tab, Col, Button, Card, Nav, Modal, Form, Table, Toast } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown'
import { Link } from 'react-router-dom';
import { auth } from '../config/Config';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProjectList } from '../redux/projectReducer';
import { useNavigate } from 'react-router-dom';
import { Triangle } from 'react-loader-spinner';
import { fetchUserList } from '../redux/userReducer';
import axios from 'axios';
import Sidebar from './SidebarNav';
import './Form.css'
import Swal from 'sweetalert2'
import Empty from './Empty';
import { useNetworkStatus } from './NetworkProvider';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { firestore } from '../config/Config';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { useForceLogout } from '../hooks/useForceLogout';

// ─── Pricing constants ────────────────────────────────────────────────────────
// CONST_BASE depends on both location and selected price type:
//   BLR  → New Price: 1970  |  Old Price: 1600
//   KKT  → New Price: 2280  |  Old Price: 2280  (same base regardless of type)
// CONST_LIMIT (floor price) is also location-aware:
//   BLR → 1980  |  KKT → 2280
const PRICE_TYPE_BASE = {
  blr: { value: 1970, oldvalue: 1600 },
  kkt: { value: 1600, oldvalue: 1600 },
};
// ─────────────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const userDetailsLocal = localStorage.getItem('userDetails');
  const userDetailsLo = JSON.parse(userDetailsLocal);

  useForceLogout();

  const dispatch = useDispatch();
  const navigation = useNavigate();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { isOnline } = useNetworkStatus();

  const [currentPage, setCurrentPage] = useState(1);
  const [lim, setLim] = useState(10);
  const [show, setShow] = useState(false);
  const [validated, setValidated] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState(0);
  const [clientEmail, setClientEmail] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [configuration, setConfiguration] = useState("");
  const [userIncrement, setUserIncrement] = useState(0);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [IsSearchBar, setIsSearchBar] = useState("")
  const [selectedPriceType, setSelectedPriceType] = useState('');

  // ─── Location-based increment rules ──────────────────────────────────────
  // BLR → base 65 + user value
  // KKT and others → base 70 + user value
  const userLocation = userDetailsLo?.location?.toLowerCase?.() || '';
  const isBLR = userLocation === 'blr';
  const isKKT = userLocation === 'kkt';
  const baseIncrement = isBLR ? 65 : 70;
  // ─────────────────────────────────────────────────────────────────────────

  // ─── Live computed values ─────────────────────────────────────────────────
  // constBase      = lookup by location + price type
  //                  BLR new=1970, BLR old=1600, KKT new/old=2280
  // finalIncrement = baseIncrement + userIncrement  (percentage)
  // totalHikedBase = constBase × (1 + finalIncrement / 100)
  // maxDiscount    = floored to nearest 5 (e.g. 37 → 35, 33 → 30)
  const locationKey      = isKKT ? 'kkt' : 'blr';
  const constBase        = selectedPriceType ? (PRICE_TYPE_BASE[locationKey]?.[selectedPriceType] ?? null) : null;
  const constLimit       = isKKT ? 2280 : 1980;  // floor price per location
  const finalIncrement   = baseIncrement + Number(userIncrement || 0);
  const totalHikedBase   = constBase ? Math.round(constBase * (1 + finalIncrement / 100)) : null;
  const computedMaxDiscount = totalHikedBase
    ? Math.floor((100 - ((constLimit / totalHikedBase) * 100)) / 5) * 5
    : null;
  // ─────────────────────────────────────────────────────────────────────────

  const { data, allData, loader, error } = useSelector((state) => state.projects);
  const user = auth.currentUser;
  const creationTime = new Date(user?.metadata.creationTime).getTime();
  const lastSignInTime = new Date(user?.metadata.lastSignInTime).getTime();

  useEffect(() => {
    const checkFirstLogin = async () => {
      try {
        const userUid = localStorage.getItem('userUID');
        const userRef = doc(firestore, 'users', userUid);
        const userSnap = await getDoc(userRef);

        const user = auth.currentUser;
        const lastSignInTime = user?.metadata?.lastSignInTime;

        if (userSnap.exists() && user) {
          const userData = userSnap.data();
          const firstLoginDone = userData.firstLoginDone;

          if (!firstLoginDone || !lastSignInTime) {
            Swal.fire({
              title: "Change Your Password!",
              html:
                `<input type="password" id="oldPassword" class="swal2-input" placeholder="Old Password">
                 <input type="password" id="newPassword" class="swal2-input" placeholder="New Password">
                 <input type="password" id="confirmPassword" class="swal2-input" placeholder="Confirm New Password">`,
              focusConfirm: false,
              showCancelButton: false,
              allowOutsideClick: false,
              allowEscapeKey: false,
              confirmButtonText: "Change Password",
              preConfirm: () => {
                const oldPassword = document.getElementById("oldPassword").value;
                const newPassword = document.getElementById("newPassword").value;
                const confirmPassword = document.getElementById("confirmPassword").value;
                if (!oldPassword || !newPassword || !confirmPassword) {
                  Swal.showValidationMessage("Please fill out all fields");
                  return false;
                }
                if (newPassword !== confirmPassword) {
                  Swal.showValidationMessage("New passwords do not match");
                  return false;
                }
                return { oldPassword, newPassword };
              },
            }).then(async (result) => {
              if (result.isConfirmed) {
                const { oldPassword, newPassword } = result.value;
                const user = auth.currentUser;
                const credential = EmailAuthProvider.credential(user.email, oldPassword);
                try {
                  await reauthenticateWithCredential(user, credential);
                  await updatePassword(user, newPassword);
                  await setDoc(doc(firestore, 'users', userUid), { firstLoginDone: true }, { merge: true });
                  Swal.fire("Success", "Password changed successfully!", "success");
                } catch (error) {
                  Swal.fire("Error", error.message, "error");
                }
              }
            });
          } else {
            console.log('User has already changed the password.');
          }
        } else {
          console.log('User document does not exist.');
        }
      } catch (error) {
        console.error("Error checking first login:", error);
      }
    };

    checkFirstLogin();
  }, []);

  const Ongoing   = allData.clients.filter(client => client.status === 'Ongoing').length;
  const completed = allData.clients.filter(client => client.status === 'completed').length;

  const { userData, userLoader, userError } = useSelector((state) => state.viewusers);
  const userUID = localStorage.getItem('userUID');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`https://us-central1-fabdiz-quote.cloudfunctions.net/api/users/${userUID}`);
        window.localStorage.setItem('userDetails', JSON.stringify(response.data));
      } catch (err) {
        console.log("error for user details", err.response ? err.response.data.error : 'Error fetching user data');
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userUID]);

  useEffect(() => {
    dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
    dispatch(fetchUserList({ userUID }));
  }, [dispatch, userUID]);

  const Swal = require('sweetalert2')

  const handleLogOut = () => {
    Swal.fire({
      position: "center",
      icon: "success",
      title: "You have logged out successfully!",
      showConfirmButton: false,
      timer: 1500
    }).then(() => {
      window.location.href = '/login';
    });
  };

  const handleClose = () => setShow(false);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
  };

  const createProject = () => {
    if (isCreatingProject) return;
    setIsCreatingProject(true);
    fetch(`https://us-central1-fabdiz-quote.cloudfunctions.net/api/project/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ID: userUID,
        pname: projectName,
        cname: clientName,
        cphone: clientPhone,
        cemail: clientEmail,
        city: city,
        state: state,
        configuration: configuration,
        date: new Date().toLocaleDateString('en-GB'),
        time: new Date().toLocaleTimeString('en-GB'),
        status: "Ongoing",
        createdBy: userDetailsLo.name,
        price: "",
        priceType: selectedPriceType,
        // ── Save computed values so AddQuotation can read them ──────────────
        database: 'pricelist04072026',
        increment: finalIncrement,
        constBase: constBase,
        constLimit: constLimit,             // 1980 for BLR, 2280 for KKT
        totalHikedBase: totalHikedBase,
        maxDiscount: computedMaxDiscount,   // already floored to nearest 5
        // ────────────────────────────────────────────────────────────────────
      }),
    })
      .then(response => response.json())
      .then(() => {
        dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
        setShow(false);
        setProjectName('');
        setClientName('');
        setClientPhone('');
        setClientEmail('');
        setCity('');
        setState('');
        setSelectedPriceType('');
        setConfiguration('');
        setUserIncrement(0);
      })
      .catch((error) => console.error('Error:', error))
      .finally(() => {
        setIsCreatingProject(false);
      });
  }

  const fetchNameList = async (value) => {
    try {
    } catch (error) {
    }
  }

  const handelChange = (value) => {
    setIsSearchBar(value)
    fetchNameList(value)
  }


  return (
    <Container fluid>
      <Row className='m-0'>
        <Col sm={1}>
        </Col>

        <Col sm={11}>

          {/* Create Project Modal */}
          <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: '26px', fontWeight: '700', color: '#5a67d8' }}>
                Create Project
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="tables">
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {[
                  { label: 'Client name',   type: 'text',   setter: setClientName },
                  { label: 'Client Phone',  type: 'number', setter: setClientPhone },
                  { label: 'Client Email',  type: 'email',  setter: setClientEmail },
                  { label: 'Property name', type: 'text',   setter: setProjectName },
                  { label: 'City',          type: 'text',   setter: setCity },
                  { label: 'State',         type: 'text',   setter: setState },
                  { label: 'Configuration', type: 'text',   setter: setConfiguration },
                ].map(({ label, type, setter }) => (
                  <Form.Group className="mb-3" key={label}>
                    <Row>
                      <Col>
                        <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                          {label}
                        </Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          type={type}
                          onChange={(e) => setter(e.target.value)}
                          required
                          style={{
                            borderRadius: '10px',
                            padding: '10px',
                            border: '1px solid #ddd',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                            backgroundColor: '#ffffff',
                          }}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                ))}

                {/* Price Type — must be selected FIRST so the increment preview is accurate */}
                <Form.Group className="mb-3">
                  <Row>
                    <Col>
                      <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                        Select Price Type
                      </Form.Label>
                    </Col>
                    <Col>
                      <Form.Select
                        value={selectedPriceType}
                        onChange={(e) => setSelectedPriceType(e.target.value)}
                        required
                        style={{
                          borderRadius: '10px',
                          padding: '10px',
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <option value="">Select</option>
                        <option value="oldvalue">Old Price</option>
                        <option value="value">New Price</option>
                      </Form.Select>
                      {constBase && (
                        <small style={{ color: '#5a67d8', marginTop: '4px', display: 'block' }}>
                          Const Base = ₹{constBase}
                        </small>
                      )}
                    </Col>
                  </Row>
                </Form.Group>

                {/* Increment field */}
                <Form.Group className="mb-3">
                  <Row>
                    <Col>
                      <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                        Increment{' '}
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: '400' }}>
                          (base {baseIncrement} + your value)
                        </span>
                      </Form.Label>
                    </Col>
                    <Col>
                      <Form.Control
                        type="number"
                        min={0}
                        placeholder={`e.g. 10 → total ${baseIncrement + 10}`}
                        value={userIncrement === 0 ? '' : userIncrement}
                        onChange={(e) => setUserIncrement(e.target.value)}
                        style={{
                          borderRadius: '10px',
                          padding: '10px',
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                          backgroundColor: '#ffffff',
                        }}
                      />

                      {/* ── Live preview: total increment + hiked base + max discount ── */}
                      {constBase ? (
                        <div style={{ marginTop: '6px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <small style={{ color: '#5a67d8', display: 'block' }}>
                            Total increment: <strong>{finalIncrement}%</strong>
                            &nbsp;→&nbsp;Hiked base: <strong>₹{totalHikedBase}</strong>
                          </small>
                          <small style={{
                            color: computedMaxDiscount < 0 ? '#e53e3e' : '#276749',
                            display: 'block',
                            fontWeight: '600',
                          }}>
                            Max discount: <strong>{computedMaxDiscount}%</strong>
                            {computedMaxDiscount < 0 && (
                              <span style={{ color: '#e53e3e', marginLeft: '6px', fontSize: '11px' }}>
                                (hiked base is below floor — increase increment)
                              </span>
                            )}
                          </small>
                        </div>
                      ) : (
                        <small style={{ color: '#aaa', marginTop: '4px', display: 'block' }}>
                          Select a price type above to see the max discount preview
                        </small>
                      )}
                      {/* ──────────────────────────────────────────────────────── */}

                    </Col>
                  </Row>
                </Form.Group>

              </Form>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: 'none' }}>
              <Button
                variant="secondary"
                onClick={handleClose}
                style={{
                  backgroundColor: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                }}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={createProject}
                disabled={isCreatingProject}
                onMouseEnter={(e) =>
                  (e.target.style.boxShadow = "0 6px 15px rgba(90, 103, 216, 0.5)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.boxShadow = "0 4px 10px rgba(90, 103, 216, 0.3)")
                }
                style={{
                  backgroundColor: '#5a67d8',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  boxShadow: '0 4px 10px rgba(90, 103, 216, 0.3)',
                  transition: 'all 0.3s ease',
                  color: '#ffffff',
                  cursor: isCreatingProject ? 'not-allowed' : 'pointer',
                  opacity: isCreatingProject ? 0.7 : 1,
                }}
              >
                {isCreatingProject ? 'Creating...' : 'Create'}
              </Button>
            </Modal.Footer>
          </Modal>

          <Row className='mt-5'>
            <Col>
              <Row className='headingPart'>
                <Col sm={2}>
                  <div>
                    <h4>Ongoing Projects</h4>
                  </div>
                </Col>
                <Col sm={2} className='headingPart1'>
                  <div>
                    <Link
                      to={'/projects'}
                      style={{
                        fontSize: '15px',
                        color: '#007bff',
                        textDecoration: 'none',
                        fontWeight: '500',
                        border: '2px solid #007bff',
                        borderRadius: '5px',
                        padding: '8px 16px',
                        display: 'inline-block',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                        e.target.style.color = '#fff';
                        e.target.style.borderColor = '#0056b3';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#007bff';
                        e.target.style.borderColor = '#007bff';
                      }}
                    >
                      View All
                    </Link>
                  </div>
                  <div>
                    <Button
                      onClick={() => setShow(true)}
                      onMouseEnter={(e) =>
                        (e.target.style.boxShadow = "0 6px 15px rgba(90, 103, 216, 0.5)")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.boxShadow = "0 4px 10px rgba(90, 103, 216, 0.3)")
                      }
                      variant="primary"
                      style={{
                        backgroundColor: "#5a67d8",
                        border: "none",
                        borderRadius: "10px",
                        boxShadow: "0 4px 10px rgba(90, 103, 216, 0.3)",
                        padding: "10px 20px",
                        marginLeft: "15px",
                        transition: "all 0.3s ease",
                        fontWeight: '600'
                      }}
                    >
                      Create New
                    </Button>
                  </div>
                </Col>
              </Row>

              <Row className='title'>
                <Col className='headerDash' sm={12}>
                  <h4>Dash Board</h4>
                </Col>
              </Row>
              <hr />
              <Row className='title1'>
                <Col className='headerDash1' sm={3}>
                  <div className='devision'>
                    <h4>Total Quotes</h4>
                  </div>
                  <hr />
                  <div className='devision1'>
                    <h4>{allData.clients.length}</h4>
                  </div>
                </Col>
                <Col className='headerDash1' sm={3}>
                  <div className='devision'>
                    <h4>On Going Quotes</h4>
                  </div>
                  <hr />
                  <div className='devision1'>
                    <h4>{Ongoing}</h4>
                  </div>
                </Col>
                <Col className='headerDash1' sm={3}>
                  <div className='devision'>
                    <h4>Completed Quotes</h4>
                  </div>
                  <hr />
                  <div className='devision1'>
                    <h4>{completed}</h4>
                  </div>
                </Col>
              </Row>
              <div className="wrapper">
              </div>
            </Col>
          </Row>

        </Col>
      </Row>
    </Container>
  );
}